import React, { useCallback, useEffect, useMemo } from 'react';
import { ElementUpdateTypes } from '@bynder-studio/render-core';
import { useTranslate } from '@bynder/localization';
import { IconLock, IconLockOpen } from '@bynder/icons';
import useEditor from 'packages/hooks/useEditor';
import useForceUpdate from 'packages/hooks/useForceUpdate';
import { collectTopLevelElements, isElementLocked } from './utils';
import useGeneralShortcuts from 'packages/hooks/shortcuts/useGeneralShortcuts';
import generateTestId from 'packages/helpers/testIdHelpers';
import { TimelineActionsIcon } from 'packages/common/editor/timeline/timeline-actions/TimelineActions.styled';
import useElementGeneralActions from 'packages/pages/editor/useElementGeneralActions';
import { parseShortcutForTooltip } from 'packages/common/WithShortcutLabel';

const testId = generateTestId('timeline_actions_lock');

export default function LockAction() {
    const { translate } = useTranslate();
    const forceUpdate = useForceUpdate();
    const { creativeModel, manipulationRenderer } = useEditor();
    const selectedElements = manipulationRenderer?.getSelectedElements() || [];

    const isActive = !!selectedElements.length;

    // todo: Remove after BE implements inherited locks
    const isSelected = isActive && collectTopLevelElements(selectedElements).every(isElementLocked);
    const { handleLock } = useElementGeneralActions();

    const handleLockChangeAction = useCallback(
        ({ updateTypes, element }) => {
            if (updateTypes.has(ElementUpdateTypes.LOCK) && selectedElements.some((el) => el.id === element.id)) {
                forceUpdate();
            }
        },
        [selectedElements, forceUpdate],
    );

    const lockedAttr = useMemo(() => ({ 'data-locked': isSelected }), [isSelected]);

    useGeneralShortcuts({ handleLock });

    useEffect(() => {
        if (!manipulationRenderer) {
            return;
        }

        manipulationRenderer.eventEmitter.on('elementSelected', forceUpdate);

        return () => {
            manipulationRenderer.eventEmitter.off('elementSelected', forceUpdate);
        };
    }, [forceUpdate, manipulationRenderer]);

    useEffect(() => {
        if (!creativeModel) {
            return;
        }

        return creativeModel.onElementsPartialUpdate(ElementUpdateTypes.LOCK, handleLockChangeAction);
    }, [creativeModel, handleLockChangeAction]);

    return (
        <div title={`${translate('editor.layers.sidebar.lock-unlock')} (${parseShortcutForTooltip(['cmd', 'l'])})`}>
            <TimelineActionsIcon
                onClick={handleLock}
                active={isActive}
                selected={isSelected}
                {...testId}
                {...lockedAttr}
            >
                {isSelected ? <IconLock /> : <IconLockOpen />}
            </TimelineActionsIcon>
        </div>
    );
}
